import React from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { formatPrice } from "../../utils/utils";

/*
 * Kartica proizvoda, v2 spec 3.2 (finalna verzija).
 * Deli se između prodavnice, početne, bend stranice i cross-sell reda.
 *
 *  ┌──────────────────┐
 *  │ slika 1:1        │  hover: druga slika ako postoji, inače scale(1.03)
 *  ├──────────────────┤
 *  │ BRKOVI           │  11px --brand uppercase → link na bend
 *  │ Majica – ...     │  16px 600, max 2 reda
 *  │ 2.200 RSD        │  17px 700
 *  └──────────────────┘
 */

const totalStock = (product) =>
  product.noSize ? product.stock || 0 : product.stockQuantity || 0;

const ProductCard = ({ product }) => {
  const navigate = useNavigate();

  const soldOut = totalStock(product) <= 0;
  const ownerName = product.band?.name || product.festival?.name || "";
  const ownerPath = product.band?.id
    ? `/bend/${product.band.slug || product.band.id}`
    : product.festival?.id
      ? `/festival/${product.festival.slug || product.festival.id}`
      : null;

  const mainImage =
    product.mainImage || product.productImages?.[0]?.url || "/placeholder.jpg";
  const hoverImage =
    product.productImages && product.productImages.length > 1
      ? product.productImages.find((img) => img.url !== mainImage)?.url
      : null;

  const goToOwner = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (ownerPath) {
      navigate(ownerPath);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 300);
    }
  };

  return (
    <Card
      to={`/proizvod/${product.slug || product.id}`}
      onClick={() => {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 300);
      }}
    >
      <ImageWrap $soldOut={soldOut} $hasHover={Boolean(hoverImage)}>
        <img
          className="primary"
          src={mainImage}
          alt={product.name}
          loading="lazy"
          decoding="async"
        />
        {hoverImage && (
          <img
            className="secondary"
            src={hoverImage}
            alt=""
            loading="lazy"
            decoding="async"
          />
        )}
        {soldOut && <SoldOutBadge>Rasprodato</SoldOutBadge>}
      </ImageWrap>

      <Body>
        {ownerName &&
          (ownerPath ? (
            <OwnerLink onClick={goToOwner}>{ownerName}</OwnerLink>
          ) : (
            <OwnerLink as="span">{ownerName}</OwnerLink>
          ))}
        <Name>{product.name}</Name>
        <PriceRow $soldOut={soldOut}>
          <span className="price">
            {formatPrice(product.salePrice || product.price)}
          </span>
          {product.salePrice && (
            <span className="old">{formatPrice(product.price)}</span>
          )}
        </PriceRow>
      </Body>
    </Card>
  );
};

export default ProductCard;

/* ------------------------------- stilovi ------------------------------- */

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
  text-decoration: none;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
    transform: translateY(-2px);
  }
`;

const ImageWrap = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  background: #ffffff;
  overflow: hidden;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition:
      opacity 0.25s ease,
      transform 0.25s ease;
    ${(p) => (p.$soldOut ? "filter: grayscale(1);" : "")}
  }

  .secondary {
    opacity: 0;
  }

  ${Card}:hover & {
    .primary {
      ${(p) =>
        p.$hasHover ? "opacity: 0;" : "transform: scale(1.03);"}
    }
    .secondary {
      opacity: 1;
    }
  }
`;

const SoldOutBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 0.25rem 0.7rem;
  background: rgba(0, 0, 0, 0.72);
  color: var(--text-primary);
  border-radius: var(--radius-pill);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.9rem 1rem 1rem;
  flex: 1;
`;

const OwnerLink = styled.span`
  width: fit-content;
  color: var(--brand);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Name = styled.h3`
  margin: 0;
  color: var(--text-primary);
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PriceRow = styled.div`
  margin-top: auto;
  padding-top: 0.35rem;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;

  .price {
    color: ${(p) => (p.$soldOut ? "var(--text-muted)" : "var(--text-primary)")};
    font-size: 17px;
    font-weight: 700;
  }

  .old {
    color: var(--text-muted);
    font-size: 0.82rem;
    text-decoration: line-through;
  }
`;
